/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {
    Box,
    SimpleGrid,
    VStack,
    Text,
    Button,
    Center,
    useMultiStyleConfig,
    useStyleConfig,
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    ADD_FILTER_HIT_COUNT,
    REMOVE_FILTER_HIT_COUNT,
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {CheckIcon} from '../../../components/icons'
import {remCalc} from '../../../theme/_utils'

const ColorRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const {formatMessage} = useIntl()
    const swatchStyles = useMultiStyleConfig('SwatchGroup', {variant: 'circle', disabled: false})
    const customStyles = useStyleConfig('ColorRefinements')

    return (
        <SimpleGrid {...customStyles?.grid}>
            {filter?.values?.map((value, idx) => {
                const isSelected = selectedFilters.includes(value?.value)
                if (value.hitCount === 0 && !isSelected) return null

                const colorValues = value?.presentationId
                    ?.split(',')
                    .map((c) => `#${c.trim().toLowerCase()}`)

                let backgroundStyle

                if (colorValues?.length === 3) {
                    backgroundStyle = `linear-gradient(to bottom right, ${colorValues[0]} 20%, ${colorValues[1]} 50%, ${colorValues[2]} 80%)`
                } else if (colorValues?.length === 2) {
                    backgroundStyle = `linear-gradient(to bottom right, ${colorValues[0]} 20%, ${colorValues[1]} 80%)`
                } else {
                    backgroundStyle = colorValues?.[0]
                }

                return (
                    <Box {...customStyles?.gridItem} key={idx}>
                        <VStack
                            onClick={() =>
                                toggleFilter(value, filter.attributeId, filter.label, isSelected)
                            }
                            cursor="pointer"
                        >
                            <Box
                                {...customStyles?.colorCircleContainer}
                                style={{border: `${isSelected ? remCalc(1) : remCalc(0)} solid`}}
                            >
                                <Button
                                    {...swatchStyles.swatch}
                                    aria-checked={isSelected}
                                    variant="outline"
                                    aria-label={formatMessage(
                                        isSelected ? REMOVE_FILTER_HIT_COUNT : ADD_FILTER_HIT_COUNT,
                                        value,
                                    )}
                                    selected={isSelected}
                                >
                                    <Center {...swatchStyles.swatchButton}>
                                        <Box
                                            {...customStyles?.colorCircle}
                                            background={backgroundStyle}
                                            position="relative"
                                        />
                                        {isSelected ? (
                                            <CheckIcon
                                                position="absolute"
                                                color={
                                                    colorValues.includes('#ffffff') ||
                                                    colorValues.includes('#fafafa')
                                                        ? 'black'
                                                        : 'white'
                                                }
                                            />
                                        ) : null}
                                    </Center>
                                </Button>
                            </Box>

                            <Box {...customStyles?.textContainer}>
                                <Text {...customStyles?.stackText}>
                                    {formatMessage(
                                        {
                                            id: 'colorRefinements.label',
                                            defaultMessage: '{colorLabel}',
                                        },
                                        {colorLabel: value?.label},
                                    )}
                                </Text>
                                <Text {...customStyles?.hitCount}>
                                    {formatMessage(
                                        {
                                            id: 'colorRefinements.hitCount',
                                            defaultMessage: '({colorHitCount})',
                                        },
                                        {colorHitCount: value?.hitCount},
                                    )}
                                </Text>
                            </Box>
                        </VStack>
                    </Box>
                )
            })}
        </SimpleGrid>
    )
}

ColorRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array,
}

export default ColorRefinements
